import React from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import {
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  Box,
  Icon,
} from '../../atoms'
import { AmenitiesData } from '@popety-io/types'
import { AmenitiesContentContainer } from './Amenities.styles'

export interface AmenitiesHeaderProps {
  data?: AmenitiesData
  className?: string
  livabilityScore?: number | null
}

/**
 * AmenitiesHeader component displays the header section with key metrics
 * for amenities analysis including POI count, search radius, and walk time.
 *
 * @param data - Amenities data containing POIs and scores
 * @param className - Optional CSS class name
 * @param livabilityScore - Optional livability score to display
 */
export const AmenitiesHeader: React.FC<AmenitiesHeaderProps> = ({
  data,
  livabilityScore,
}) => {
  const { t } = useTranslation('amenities')

  const formatRadius = (radius: number): string => {
    if (radius < 1000) {
      return `${radius}m`
    }
    return `${(radius / 1000).toFixed(1)}km`
  }

  const formatWalkTime = (radius: number): string => {
    // Approximate walking time (5 km/h = 83.3 m/min)
    const minutes = Math.round(radius / 83.3)
    return `${minutes} min`
  }

  const getScoreVariant = (
    category: string
  ): 'success' | 'warning' | 'danger' | 'default' => {
    switch (category) {
      case 'excellent':
        return 'success'
      case 'good':
        return 'default'
      case 'fair':
        return 'warning'
      case 'poor':
        return 'danger'
      default:
        return 'default'
    }
  }

  // Add null checks to prevent errors
  if (!data) {
    return (
      <>
        <AnalysisSectionHeader title={t('title')} subtitle={t('subtitle')} />
        <AnalysisMetricsGrid>
          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#3b82f6">
                <Icon name="map-pin" size="small" />
              </MetricIcon>
              <MetricTitle>{t('pointsOfInterest')}</MetricTitle>
            </MetricHeader>
            <Box style={{ marginTop: '8px' }}>
              <MetricValue>0</MetricValue>
            </Box>
          </AnalysisMetricCard>
        </AnalysisMetricsGrid>
      </>
    )
  }

  return (
    <>
      <AnalysisSectionHeader
        title={t('title')}
        subtitle={t('subtitle')}
        score={
          livabilityScore
            ? {
                value: livabilityScore,
                maxValue: data.overall_score.max_value,
                label: t('livabilityScore'),
                variant: getScoreVariant(data.overall_score.category),
                tooltip: data.overall_score.description,
                showProgress: true,
              }
            : undefined
        }
      />
      <AmenitiesContentContainer>
        <AnalysisMetricsGrid>
          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#3b82f6">
                <Icon name="map-pin" size="small" />
              </MetricIcon>
              <MetricTitle>{t('pointsOfInterest')}</MetricTitle>
            </MetricHeader>
            <Box style={{ marginTop: '8px' }}>
              <MetricValue>{data.total_pois || 0}</MetricValue>
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#8b5cf6">
                <Icon name="map" size="small" />
              </MetricIcon>
              <MetricTitle>{t('radius')}</MetricTitle>
            </MetricHeader>
            <Box style={{ marginTop: '8px' }}>
              <MetricValue>{formatRadius(data.search_radius || 0)}</MetricValue>
            </Box>
          </AnalysisMetricCard>

          <AnalysisMetricCard>
            <MetricHeader>
              <MetricIcon color="#10b981">
                <Icon name="user" size="small" />
              </MetricIcon>
              <MetricTitle>{t('walkTime')}</MetricTitle>
            </MetricHeader>
            <Box style={{ marginTop: '8px' }}>
              <MetricValue>{formatWalkTime(data.walking_radius)}</MetricValue>
            </Box>
          </AnalysisMetricCard>
        </AnalysisMetricsGrid>
      </AmenitiesContentContainer>
    </>
  )
}

export default AmenitiesHeader
